import { BASE_URL } from '@yld/yun-utils-host'
import { store } from '@geek/yum-freight-framework'
import { tosaveSpotSearchRecord } from '@/api/ecomRates'
import { gotoEcomBooking } from '@/utils/ecomBookingRoute'

/** iframe 子页 postMessage 类型：运价查询 */
export const MSG_TYPE_GEEKYUM_RATE_QUERY = 'GEEKYUM_RATE_QUERY'
/** iframe 子页请求父页跳转订舱页 */
export const MSG_TYPE_GOTO_BOOKING = 'IFMS_GOTO_BOOKING'
/** 父页通知子页切换船司 Tab */
export const MSG_TYPE_CARRIER_TAB_CHANGE = 'IFMS_CARRIER_TAB_CHANGE'
/** 父页同步 IFMS 内嵌上下文（费用明细弹窗等依赖 iframe_source） */
export const MSG_TYPE_EMBED_CONTEXT = 'IFMS_EMBED_CONTEXT'
export const IFMS_PARENT_MSG_SOURCE = 'IFMS_PARENT'
export const IFMS_IFRAME_MSG_SOURCE = 'IFMS_RATE_IFRAME'
export const IFMS_BOOKING_RATE_KEY = 'IFMS_BOOKING_RATE'
export const RATE_QUERY_KEY = 'RATE_QUERY'
/** 关闭电商运价菜单（tags）后，下次打开需初始化搜索 */
export const ECOM_RATES_FORCE_RESET_KEY = 'ECOM_RATES_FORCE_RESET'
/** 订舱页点「返回运价列表」时，强制恢复查询列表 */
export const ECOM_RATES_BACK_TO_LIST_KEY = 'ECOM_RATES_BACK_TO_LIST'
/** 父页当前船司 Tab（全部/MSK/CMA），与 RATE_QUERY 同步维护 */
export const ECOM_RATES_CARRIER_TAB_KEY = 'ECOM_RATES_CARRIER_TAB'
/** iframe 已进入运价列表（postMessage 同步，跨域无法读 iframe location） */
export const ECOM_RATES_ON_LIST_KEY = 'ECOM_RATES_ON_LIST'
/** 直连专区（CMA）查询结果快照：订舱页返回、切换 Tab 后恢复列表，不用重新搜索（CMA 查价计调用额度） */
export const ECOM_RATES_CMA_SNAPSHOT_KEY = 'ECOM_RATES_CMA_SNAPSHOT'
let unbindSpotIframeMessage = null
let unbindEcomRatesTagWatch = null
let lastGotoBookingKey = ''
let lastGotoBookingAt = 0
const GOTO_BOOKING_DEDUPE_MS = 800
const IFRAME_LOAD_GRACE_MS = 2000
let lastEcomRatesIframeLoadAt = 0
let ecomRatesIframeHandlers = {
  getIframeSrc: () => '',
  syncIframeSrcFromCache: null,
  scheduleSessionRestoreReload: null
}

function isEcomRatesView(view) {
  if (!view) return false
  if (view.name === 'ecomRates' || view.name === 'EcomRates') return true
  return typeof view.path === 'string' && view.path.includes('/ecomRates')
}

function isEcomBookingView(view) {
  if (!view) return false
  if (view.name === 'ecomBooking' || view.name === 'EcomBooking') return true
  return typeof view.path === 'string' && view.path.includes('/ecomBooking')
}

const RATE_IFRAME_ORIGIN_QA = 'https://qa-rateiframe.geekyum.com'
const RATE_IFRAME_ORIGIN_PRO = 'https://gigarate.htholding.cn'

export function getRateIframeOrigin() {
  return BASE_URL.PREFIX_DOT ? RATE_IFRAME_ORIGIN_QA : RATE_IFRAME_ORIGIN_PRO
}

/** 90042 父页面 origin，供 iframe 回传 postMessage 使用 */
export function getParentPageOrigin() {
  return window.location.origin || ''
}

export function isRateIframeOrigin(origin) {
  if (!origin) return false
  if (origin === getRateIframeOrigin()) return true
  if (process.env.NODE_ENV === 'development') {
    return /^https?:\/\/[\w.-]+(:\d+)?$/.test(origin) &&
      (origin.includes('localhost') || origin.includes('geekyum.com') || origin.includes('htholding.cn'))
  }
  return false
}

export function buildRateIframeParams(options = {}) {
  const {
    lang = 'zh',
    theme = 'FF862D',
    query = {},
    userInfo = {},
    externalUserId = '',
    carrierCode = '',
    omitPortCodes = false
  } = options
  const themeHex = String(theme || '').replace(/^#/, '')
  const params = new URLSearchParams({
    lang: String(lang || ''),
    iframe_theme: themeHex,
    ctn: String((query.containerTypes || query.containerType || []).toString()),
    iframe_source: 'IFMS',
    ifmsBookingEntry: '1',
    externalUserId: String(externalUserId || ''),
    geekyumenv: getParentPageOrigin(),
    userInfo: JSON.stringify(userInfo || {}),
    carrierCode: String(carrierCode || '')
  })
  if (!omitPortCodes) {
    const pol = String(query.porCode || query.polCode || '')
    const pod = String(query.pdlCode || query.podCode || '')
    if (pol) params.set('pol', pol)
    if (pod) params.set('pod', pod)
  }
  return params
}

/** 从父页 iframe src 解析 pol/pod（用于校验 postMessage 是否与 URL 一致） */
export function parsePortCodesFromIframeSrc(src = '') {
  if (!src || typeof src !== 'string') return { pol: '', pod: '' }
  try {
    const url = new URL(src)
    return {
      pol: url.searchParams.get('pol') || '',
      pod: url.searchParams.get('pod') || ''
    }
  } catch (e) {
    const polMatch = src.match(/[?&]pol=([^&]+)/)
    const podMatch = src.match(/[?&]pod=([^&]+)/)
    return {
      pol: polMatch ? decodeURIComponent(polMatch[1]) : '',
      pod: podMatch ? decodeURIComponent(podMatch[1]) : ''
    }
  }
}

export function getCachedRateQuery() {
  try {
    const raw = sessionStorage.getItem(RATE_QUERY_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function markEcomRatesIframeLoaded() {
  lastEcomRatesIframeLoadAt = Date.now()
}

export function registerEcomRatesIframeHandlers(handlers = {}) {
  ecomRatesIframeHandlers = {
    ...ecomRatesIframeHandlers,
    ...handlers
  }
}

function buildCacheFromRateQuery(query = {}) {
  let carrierCode = sessionStorage.getItem(ECOM_RATES_CARRIER_TAB_KEY) ?? ''
  try {
    const prev = getCachedRateQuery()
    if (prev && prev.carrierCode !== undefined && prev.carrierCode !== null) {
      carrierCode = prev.carrierCode
    }
  } catch (e) {
    // ignore
  }
  return {
    polCode: query.polCode || '',
    podCode: query.podCode || '',
    porCode: query.polCode || '',
    pdlCode: query.podCode || '',
    containerType: query.containerType || '',
    containerTypes: query.containerType
      ? String(query.containerType).split(',').filter(Boolean)
      : [],
    carrierCode
  }
}

function normalizeContainerKey(query = {}) {
  const raw = query.containerType ||
    (Array.isArray(query.containerTypes) ? query.containerTypes.join(',') : '')
  return String(raw || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .sort()
    .join(',')
}

function rateQueriesMatchPorts(a, b) {
  if (!a || !b) return false
  const aPol = a.porCode || a.polCode || ''
  const aPod = a.pdlCode || a.podCode || ''
  const bPol = b.porCode || b.polCode || ''
  const bPod = b.pdlCode || b.podCode || ''
  return aPol === bPol && aPod === bPod
}

function rateQueriesMatch(a, b) {
  return rateQueriesMatchPorts(a, b) && normalizeContainerKey(a) === normalizeContainerKey(b)
}

/** 父页通知 iframe 切换船司筛选 */
export function notifyIframeCarrierTab(iframeEl, carrierCode) {
  const win = iframeEl?.contentWindow
  if (!win) return
  win.postMessage(
    {
      type: MSG_TYPE_CARRIER_TAB_CHANGE,
      source: IFMS_PARENT_MSG_SOURCE,
      payload: { carrierCode: carrierCode || '' }
    },
    getRateIframeOrigin()
  )
}

/** 父页同步 IFMS 内嵌标识、主题色与订舱入口 */
export function notifyIframeEmbedContext(iframeEl, options = {}) {
  const win = iframeEl?.contentWindow
  if (!win) return
  const theme = options.theme || 'FF862D'
  const themeHex = String(theme || '').replace(/^#/, '')
  win.postMessage(
    {
      type: MSG_TYPE_EMBED_CONTEXT,
      source: IFMS_PARENT_MSG_SOURCE,
      payload: {
        iframe_source: 'IFMS',
        iframe_theme: themeHex,
        ifmsBookingEntry: '1',
        carrierCode: options.carrierCode || ''
      }
    },
    getRateIframeOrigin()
  )
}

function shouldSkipDuplicateGotoBooking(payload = {}) {
  const key = [
    payload.md5Key || '',
    payload.containerType ||
      (Array.isArray(payload.containerTypes) ? payload.containerTypes.join(',') : ''),
    payload.spuId || ''
  ].join('|')
  if (!key || key === '||') return false
  const now = Date.now()
  if (key === lastGotoBookingKey && now - lastGotoBookingAt < GOTO_BOOKING_DEDUPE_MS) {
    return true
  }
  lastGotoBookingKey = key
  lastGotoBookingAt = now
  return false
}

/**
 * 监听运价 iframe 的 postMessage（90042 管理系统侧逻辑）。
 * @param {Vue} vm 当前页面实例，用于路由跳转
 * @returns {Function} 卸载监听器
 */
export function bindSpotIframeMessage(vm) {
  const onMessage = async(event) => {
    if (!isRateIframeOrigin(event.origin)) return
    const raw = event.data
    if (!raw || typeof raw !== 'object') return

    if (raw.type === MSG_TYPE_GOTO_BOOKING) {
      if (raw.source !== IFMS_IFRAME_MSG_SOURCE) return
      const payload = raw.payload || {}
      if (shouldSkipDuplicateGotoBooking(payload)) return
      const bookingPayload = {
        md5Key: payload.md5Key || '',
        containerType: payload.containerType ||
          (Array.isArray(payload.containerTypes) ? payload.containerTypes.join(',') : ''),
        containerTypes: Array.isArray(payload.containerTypes)
          ? payload.containerTypes
          : (payload.containerType ? String(payload.containerType).split(',').filter(Boolean) : []),
        spuId: payload.spuId || null,
        carrierCode: payload.carrierCode || '',
        bookingToken: Date.now()
      }
      if (bookingPayload.md5Key) {
        sessionStorage.setItem(IFMS_BOOKING_RATE_KEY, JSON.stringify(bookingPayload))
      }
      await gotoEcomBooking(vm, bookingPayload)
      return
    }

    if (raw.type !== MSG_TYPE_GEEKYUM_RATE_QUERY) return

    const query = raw.payload?.query
    if (!query) return

    const incomingPol = query.polCode || ''
    const incomingPod = query.podCode || ''
    const cached = getCachedRateQuery()
    const iframeSrc = ecomRatesIframeHandlers.getIframeSrc?.() || ''
    const srcPorts = parsePortCodesFromIframeSrc(iframeSrc)

    // 父页 src 已带 pol/pod 时，忽略与 URL 不一致的 postMessage（子页 sessionStorage 残留）
    if (srcPorts.pol && srcPorts.pod) {
      if (incomingPol !== srcPorts.pol || incomingPod !== srcPorts.pod) {
        return
      }
    }

    const incomingCache = buildCacheFromRateQuery(query)
    const onList = sessionStorage.getItem(ECOM_RATES_ON_LIST_KEY) === '1'
    const sinceLoad = Date.now() - lastEcomRatesIframeLoadAt

    // 90060 searchRate 会先 postMessage 上一次查询：港口+箱型都相同则忽略，避免冲掉二次搜索
    if (onList && cached && rateQueriesMatch(cached, incomingCache)) {
      return
    }

    sessionStorage.setItem(RATE_QUERY_KEY, JSON.stringify(incomingCache))
    sessionStorage.setItem(ECOM_RATES_ON_LIST_KEY, '1')
    // 已在列表内：勿 reload iframe（会冲掉二次搜索结果）；仅首次进列表时同步 src
    if (!onList || sinceLoad < IFRAME_LOAD_GRACE_MS) {
      ecomRatesIframeHandlers.syncIframeSrcFromCache?.(incomingCache)
    }

    const userInfo = store.getters.userInfo || {}
    await tosaveSpotSearchRecord({
      containers: query.containerType,
      hasPrice: 1,
      opsUserId: userInfo.id,
      platformType: 'PC',
      podCode: query.podCode,
      podName: query.podName,
      polCode: query.polCode,
      polName: query.polName,
      tenantId: userInfo.tenantId
    })
  }

  window.addEventListener('message', onMessage)
  return () => window.removeEventListener('message', onMessage)
}

/** 全局注册一次，避免只在 ecomRates 页面挂载时漏接消息 */
export function ensureSpotIframeMessageListener(vm) {
  if (unbindSpotIframeMessage) return unbindSpotIframeMessage
  unbindSpotIframeMessage = bindSpotIframeMessage(vm)
  return unbindSpotIframeMessage
}

/**
 * 监听面包屑 tags：仅「关闭电商运价菜单」时标记下次初始化。
 * 面包屑切换不会移除 tag，因此不会触发重置。
 */
export function ensureEcomRatesTagCloseReset() {
  if (unbindEcomRatesTagWatch) return unbindEcomRatesTagWatch
  if (!store?.watch) return () => {}

  unbindEcomRatesTagWatch = store.watch(
    (state) => (state.tagsView && state.tagsView.visitedViews) || [],
    (views, oldViews) => {
      const wasOpen = (oldViews || []).some(isEcomRatesView)
      const isOpen = (views || []).some(isEcomRatesView)
      if (wasOpen && !isOpen) {
        sessionStorage.removeItem(RATE_QUERY_KEY)
        sessionStorage.removeItem(ECOM_RATES_ON_LIST_KEY)
        sessionStorage.removeItem(ECOM_RATES_CMA_SNAPSHOT_KEY)
        sessionStorage.setItem(ECOM_RATES_FORCE_RESET_KEY, '1')
      }
    }
  )
  return unbindEcomRatesTagWatch
}

/**
 * 电商运价 / 订舱互切时开启 keepAlive，避免 iframe 列表状态被销毁。
 * AppMain 仅在当前路由 meta.keepAlive 为 true 时走 keep-alive 出口，
 * 因此两边都需要打开，否则切到订舱仍会卸载运价页。
 */
export function ensureEcomRatesKeepAlive(router) {
  if (!router?.beforeEach || router.__ecomKeepAlivePatched) return
  router.__ecomKeepAlivePatched = true
  router.beforeEach((to, from, next) => {
    if (isEcomRatesView(to) || isEcomBookingView(to)) {
      if (to.meta) {
        to.meta.keepAlive = true
      }
      (to.matched || []).forEach((record) => {
        if (record?.meta) {
          record.meta.keepAlive = true
        }
      })
    }
    next()
  })
}
